import { Flame, Ruler, FlaskRound, ShieldCheck } from "lucide-react";
import { Reveal, SectionHead, PageHero, SpecTable, CTASection, usePageMeta } from "../components/ui-bits";
import { IMG, imgUrl, scaleMetrics, ndtCapabilities, mechanicalTests, bays } from "../data/content";
import { useCmsList } from "../hooks/useCmsContent";

const ICONS = { Ruler, Flame, FlaskRound, ShieldCheck };

const toSpecRows = (items) => (items || []).map((it) => (Array.isArray(it) ? it : [it.label, it.value]));

// Static fallback mirrors the bundled content exactly — used until/if CMS content is available.
const SECTION_FALLBACK = [
  { id: "fabrication", icon: "Ruler", kicker: "Fabrication", title: "Large vessels, columns & process equipment", flip: false,
    img: "columns-hmel-b", img_alt: "Heavy column under fabrication", metrics: "scale", bullets: [], spec_rows: [],
    body: "Shop fabrication of pressure vessels up to 6.5 m diameter and 425 MT single-piece, and columns up to 6.8 m diameter and 110 m long — across three dedicated fabrication bays at Tarapur works, including a 27 m × 160 m heavy bay." },
  { id: "welding", icon: "Flame", kicker: "Welding", title: "Controlled welding for code-critical joints", flip: true,
    img: "rr-a", img_alt: "Reactor package under fabrication", metrics: "", spec_rows: [],
    body: "Welding technology is a dedicated function at Tarapur works, with qualified procedures and welders for carbon steel, low-alloy steel, stainless, clad, duplex, super duplex and titanium construction — under ASME Section VIII / Section IX discipline for 'U' and 'U2' stamped equipment.",
    bullets: ["Qualified WPS / PQR across CS, LAS, SS, clad, duplex & titanium", "Weld mapping, traceability and staged hold-point inspection", "Supported by in-house NDT and mechanical testing at every stage"] },
  { id: "ndt-inspection", icon: "FlaskRound", kicker: "NDT & Inspection", title: "Fourteen in-house examination methods", flip: false,
    img: "exchanger-branded", img_alt: "Heat exchanger under inspection at G. R. Engineering works", metrics: "ndt", bullets: [], spec_rows: [],
    body: "A separate radiography enclosure, gamma ray sources (Cobalt 60, Ir-192), advanced ultrasonic methods and leak testing — all in-house, all under the quality plan of each job." },
  { id: "heat-treatment", icon: "Flame", kicker: "Heat Treatment", title: "PWHT / PFHT for the largest fabrications", flip: true,
    img: "mounded-site", img_alt: "Mounded storage vessels at site", metrics: "", bullets: [],
    spec_rows: [["Furnace Type", "Bogie hearth, LDO fired"], ["Furnace Size", "6 m H × 6 m W × 12 m L"], ["Processes", "PWHT / PFHT"], ["Recording", "Time vs. temperature charts"]],
    body: "Post-weld and post-forming heat treatment in a diesel (LDO) fired bogie hearth furnace sized for complete vessels — with time–temperature recording for code compliance." },
  { id: "mechanical-testing", icon: "ShieldCheck", kicker: "Mechanical Testing", title: "In-house NABL-accredited laboratory", flip: false,
    img: "metering-skid", img_alt: "Precision skid fabricated by G. R. Engineering", metrics: "mechanical", bullets: [], spec_rows: [],
    body: "Destructive testing in-house — from ambient-temperature tensiles to impact testing at −195 °C — closing the loop between procedure qualification and production welds." },
];

const CapabilityBlock = ({ icon: Icon, kicker, title, children, img, flip }) => (
  <div className="border-b border-slate-200 last:border-b-0" data-testid={`cap-block-${kicker.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="container-gre py-16 lg:py-20 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
      <div className={flip ? "lg:order-2" : ""}>
        <Reveal>
          <div className="flex items-center gap-3">
            <Icon size={22} className="text-[#C5221F]" strokeWidth={1.75} />
            <span className="font-mono-tech text-xs font-semibold uppercase tracking-[0.25em] text-[#C5221F]">{kicker}</span>
          </div>
        </Reveal>
        <Reveal delay={0.07}>
          <h2 className="font-display mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
        </Reveal>
        <div className="mt-6">{children}</div>
      </div>
      <Reveal delay={0.1} className={flip ? "lg:order-1" : ""}>
        {img}
      </Reveal>
    </div>
  </div>
);

const Capabilities = () => {
  usePageMeta(
    "Engineering & Manufacturing Capabilities | G. R. Engineering Private Limited",
    "Heavy fabrication of pressure vessels, columns and towers; controlled welding; in-house NDT (RT, UT, TOFD, PAUT, MT, PT, PMI); bogie hearth heat treatment; NABL-accredited mechanical testing."
  );
  const sections = useCmsList("capability_section", SECTION_FALLBACK);
  const rawGroups = useCmsList("scale_group", scaleMetrics);
  const groups = rawGroups.map((g) => ({ group: g.group, items: toSpecRows(g.items) }));
  const bayList = useCmsList("facility_bay", bays);
  return (
    <>
      <PageHero
        img={IMG("site-fab-vessel")}
        eyebrow="Engineering & Manufacturing"
        title="Where heavy engineering becomes hardware"
        sub="Fabrication, welding, NDT, heat treatment and mechanical testing — the complete technical backbone behind every G. R. Engineering delivery."
      />

      <section className="bg-white" data-testid="capability-blocks">
        {sections.map((s) => {
          const Icon = ICONS[s.icon] || Ruler;
          return (
            <CapabilityBlock
              key={s.id || s.kicker}
              icon={Icon}
              kicker={s.kicker}
              title={s.title}
              flip={!!s.flip}
              img={<img src={imgUrl(s.img)} alt={s.img_alt || s.title} loading="lazy" className="w-full aspect-[4/3] object-cover img-industrial border border-slate-200" />}
            >
              {s.body && <p className="text-base leading-relaxed text-slate-600">{s.body}</p>}
              {s.metrics === "scale" && (
                <div className="mt-7 grid sm:grid-cols-2 gap-6">
                  {groups.map((g) => (
                    <div key={g.group} className="border border-slate-200">
                      <p className="px-5 py-3 bg-slate-50 border-b border-slate-200 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-500">{g.group} — Max Parameters</p>
                      <SpecTable rows={g.items} />
                    </div>
                  ))}
                </div>
              )}
              {s.metrics === "ndt" && (
                <div className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {ndtCapabilities.map((n) => (
                    <p key={n} className="flex items-start gap-2.5 text-[13px] text-slate-700"><span className="mt-1.5 w-1.5 h-1.5 bg-[#C5221F] shrink-0" />{n}</p>
                  ))}
                </div>
              )}
              {s.metrics === "mechanical" && (
                <div className="mt-7 max-w-md">
                  <SpecTable rows={mechanicalTests} />
                </div>
              )}
              {!!(s.bullets || []).length && (
                <ul className="mt-6 space-y-3">
                  {s.bullets.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-slate-700"><span className="mt-1.5 w-2 h-2 bg-[#C5221F] shrink-0" />{t}</li>
                  ))}
                </ul>
              )}
              {!!(s.spec_rows || []).length && (
                <div className="mt-7 max-w-md">
                  <SpecTable rows={s.spec_rows} />
                </div>
              )}
            </CapabilityBlock>
          );
        })}
      </section>

      <section className="bg-[#0A1128] relative overflow-hidden" data-testid="capabilities-bays">
        <div className="absolute inset-0 blueprint-grid" />
        <div className="container-gre relative py-20 lg:py-24">
          <SectionHead light eyebrow="Fabrication Bays — Tarapur Works" title="Three bays, one heavy-industrial campus" />
          <div className="mt-12 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {bayList.map((b, i) => (
              <Reveal key={b.bay} delay={i * 0.08}>
                <div className="bg-[#0A1128]/70 backdrop-blur-sm p-8 h-full">
                  <p className="font-display text-lg font-bold text-white">{b.bay}</p>
                  <p className="mt-3 font-display text-2xl font-black text-[#E8574F] tracking-tight">{b.size}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Capabilities;
